import { useState, useLayoutEffect } from "react";
import CSDTable from "@cloudscape-design/components-themed/table";
import { CONFIGS, LABELS } from "./constants";
import { Box } from "@cloudscape-design/components-themed";
import Preference from "../UI/Preference";
import { COMMON_LABELS } from "../default-constants";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import Pagination from "../UI/Pagination";
import { ConversionToolError } from "../../features/api-interfaces";
import { Messages, getMessage } from "../../features/error/messages";

export interface MessageItem {
  no: number;
  messageLabel: string;
  errorOccurredPlace: string;
  errorFileName: string;
}

/**
 * エラーメッセージ作成処理
 * @param messageList メッセージリスト
 * @returns エラーメッセージリスト
 */
export const getMessageItemList = (messageList: ConversionToolError[]) => {
  // エラーメッセージリスト作成
  let errorMessageList: MessageItem[] = [];

  for (const [index, message] of messageList.entries()) {
    const msgId = message.message.messageId as keyof Messages;
    const messageStr = getMessage(msgId, ...message.message.messageArgs).label;
    const separatorIndex = messageStr.lastIndexOf(CONFIGS.SEPARATOR);

    const errorData = {
      no: index + 1,
      messageLabel: messageStr.slice(0, separatorIndex),
      errorOccurredPlace:
        separatorIndex === -1
          ? ""
          : messageStr.slice(separatorIndex, messageStr.length),
      errorFileName: message.fileName,
    } as MessageItem;

    errorMessageList.push(errorData);
  }

  return errorMessageList;
};

/**
 * エラー発生時のテーブルを表示する。
 *
 * @param props.data エラーデータリスト（MessageItem[]型）
 * @param props.title エラー表示タイトル
 * @returns エラーメッセージ表示テーブル
 */
const ErrorTable = (props: any) => {
  const columnDefinitions = [
    {
      id: "no",
      header: LABELS.NO,
      cell: (e: any) => e.no,
      width: 110,
    },
    {
      id: "messageLabel",
      header: LABELS.ERROR_DETAIL,
      cell: (e: any) => e.messageLabel,
    },
    {
      id: "errorFileName",
      header: LABELS.ERROR_FILENAME,
      cell: (e: any) => e.errorFileName,
    },
    {
      id: "errorOccurredPlace",
      header: LABELS.ERROR_OCCURRED_PLACE,
      cell: (e: any) => e.errorOccurredPlace,
    },
  ];
  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>(
    getPagedItems(props.data, 1, DEFAULT_PAGE_SIZE)
  );
  const [allItems, setAllItems] = useState<Object[]>(props.data);

  useLayoutEffect(() => {
    if (props.data !== undefined) {
      setPagedItems(getPagedItems(props.data, 1, DEFAULT_PAGE_SIZE));
      setAllItems(props.data);
      setCurrentPageIndex(1);
    }
  }, [props.data]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    if (props.data !== undefined) {
      setPagedItems(props.data.slice(0, newPageSize));
      setCurrentPageIndex(1);
    }
  };

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      items={pagedItems}
      resizableColumns
      stickyHeader
      header={
        <Box color="text-status-error" variant="h2">
          {props.title}
          {getHeaderCounterText(
            allItems.length,
            pagedItems.length + (currentPageIndex - 1) * preferences.pageSize
          )}
        </Box>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={preferences.pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={preferences}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default ErrorTable;
